import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/useAuth';
import { useOrg } from '../../../context/OrgContext';
import {
  createOrganizationApi,
  getMyOrganizationsApi,
  updateOrganizationApi,
  deleteOrganizationApi,
  getOrganizationMembersApi,
  addOrganizationMemberApi,
  updateOrganizationMemberRoleApi,
  removeOrganizationMemberApi,
} from '../../../api/apiServices';

const MANAGE_ROLES = ['owner', 'admin'];
const ALL_ROLES = ['owner', 'admin', 'instructor', 'assistant'];

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  instructor: 'Instructor',
  assistant: 'Assistant',
};

const ROLE_STYLES = {
  owner: 'bg-amber-50 text-amber-700 border-amber-200',
  admin: 'bg-purple-50 text-purple-700 border-purple-200',
  instructor: 'bg-teal-50 text-teal-700 border-teal-200',
  assistant: 'bg-slate-100 text-slate-600 border-slate-200',
};

const slugPreview = (name) =>
  (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

export default function AdminOrganizationPage() {
  const { user } = useAuth();
  const myUserId = user?.userId;
  // Creating an org here should immediately make it the sidebar's active
  // context -- without this, a course created right after org creation
  // still gets organizationId: null (whatever "Personal"/previous org was
  // selected) since OrgContext's currentOrgId never auto-follows a newly
  // created org on its own.
  const { setCurrentOrgId: setSidebarOrgId, refreshOrganizations: refreshSidebarOrganizations } = useOrg();

  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
  };

  // Create-organization form (shown when the user has none yet)
  const [createForm, setCreateForm] = useState({ name: '', slug: '', logoUrl: '' });
  const [createLogoFile, setCreateLogoFile] = useState(null);
  const [createLogoPreview, setCreateLogoPreview] = useState('');

  // Settings form for the currently-selected organization
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsLogoFile, setSettingsLogoFile] = useState(null);
  const [settingsLogoPreview, setSettingsLogoPreview] = useState('');
  const [settingsBgImageFile, setSettingsBgImageFile] = useState(null);
  const [settingsBgImagePreview, setSettingsBgImagePreview] = useState('');

  const handleCreateLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCreateLogoFile(file);
    setCreateLogoPreview(URL.createObjectURL(file));
  };

  const handleSettingsLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSettingsLogoFile(file);
    setSettingsLogoPreview(URL.createObjectURL(file));
  };

  const handleSettingsBgImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSettingsBgImageFile(file);
    setSettingsBgImagePreview(URL.createObjectURL(file));
  };

  // Add-member form
  const [memberForm, setMemberForm] = useState({ email: '', role: 'instructor' });

  const selectedOrg = organizations.find(o => o._id === selectedOrgId) || null;
  const myRole = selectedOrg?.myRole;
  const canManage = MANAGE_ROLES.includes(myRole);
  const isOwner = myRole === 'owner';

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await getMyOrganizationsApi();
      if (response.success) {
        const orgs = response.organizations || [];
        setOrganizations(orgs);
        setSelectedOrgId(prev => (orgs.some(o => o._id === prev) ? prev : orgs[0]?._id || null));
      } else {
        showNotification('error', response.message || 'Failed to load your organizations');
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      showNotification('error', 'Failed to load your organizations. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      setSettingsForm({
        name: selectedOrg.name,
        logoUrl: selectedOrg.logoUrl || '',
        primaryColor: selectedOrg.theme?.primaryColor || '#0d9488',
        backgroundColor: selectedOrg.theme?.backgroundColor || '#0f172a',
        backgroundImageUrl: selectedOrg.theme?.backgroundImageUrl || '',
        certificateTitle: selectedOrg.certificate?.title || 'Certificate of Completion',
        certificateMessage: selectedOrg.certificate?.message || 'has successfully completed all requirements for the course',
        certificateSignatureName: selectedOrg.certificate?.signatureName || '',
        certificateSignatureTitle: selectedOrg.certificate?.signatureTitle || 'Course Instructor',
      });
      // A freshly picked file belonged to whichever org was selected before
      // -- clear it when switching so it's never accidentally applied to a
      // different organization.
      setSettingsLogoFile(null);
      setSettingsLogoPreview('');
      setSettingsBgImageFile(null);
      setSettingsBgImagePreview('');
      loadMembers(selectedOrg._id);
    } else {
      setSettingsForm(null);
      setMembers([]);
    }
  }, [selectedOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMembers = async (orgId) => {
    try {
      setMembersLoading(true);
      const response = await getOrganizationMembersApi(orgId);
      if (response.success) {
        setMembers(response.members || []);
      } else {
        showNotification('error', response.message || 'Failed to load members');
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
      showNotification('error', 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      showNotification('error', 'Give your organization a name first');
      return;
    }
    try {
      setSaving(true);
      const response = await createOrganizationApi(
        {
          name: createForm.name.trim(),
          slug: createForm.slug.trim() || undefined,
          logoUrl: createForm.logoUrl.trim() || undefined,
        },
        { logo: createLogoFile || undefined }
      );
      if (response.success) {
        showNotification('success', 'Organization created! You can now customize it and invite your team.');
        setCreateForm({ name: '', slug: '', logoUrl: '' });
        setCreateLogoFile(null);
        setCreateLogoPreview('');
        // Make the new org the sidebar's active context so courses created
        // right after this land in it instead of "Personal".
        if (response.organization?._id) {
          setSidebarOrgId(response.organization._id);
        }
        await Promise.all([loadOrganizations(), refreshSidebarOrganizations()]);
      } else {
        showNotification('error', response.message || 'Failed to create organization');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      setSaving(true);
      const response = await updateOrganizationApi(
        selectedOrg._id,
        {
          name: settingsForm.name.trim(),
          logoUrl: settingsForm.logoUrl.trim(),
          theme: {
            primaryColor: settingsForm.primaryColor,
            backgroundColor: settingsForm.backgroundColor,
          },
          certificate: {
            title: settingsForm.certificateTitle.trim() || 'Certificate of Completion',
            message: settingsForm.certificateMessage.trim() || 'has successfully completed all requirements for the course',
            signatureName: settingsForm.certificateSignatureName.trim(),
            signatureTitle: settingsForm.certificateSignatureTitle.trim() || 'Course Instructor',
          },
        },
        {
          logo: settingsLogoFile || undefined,
          backgroundImage: settingsBgImageFile || undefined,
        }
      );
      if (response.success) {
        showNotification('success', 'Organization updated successfully');
        setSettingsLogoFile(null);
        setSettingsLogoPreview('');
        setSettingsBgImageFile(null);
        setSettingsBgImagePreview('');
        await Promise.all([loadOrganizations(), refreshSidebarOrganizations()]);
      } else {
        showNotification('error', response.message || 'Failed to update organization');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !memberForm.email.trim()) return;
    try {
      setSaving(true);
      const response = await addOrganizationMemberApi(selectedOrg._id, {
        email: memberForm.email.trim(),
        role: memberForm.role,
      });
      if (response.success) {
        showNotification('success', 'Member added successfully');
        setMemberForm({ email: '', role: 'instructor' });
        await loadMembers(selectedOrg._id);
      } else {
        showNotification('error', response.message || 'Failed to add member');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (targetUserId, role) => {
    if (!selectedOrg) return;
    try {
      const response = await updateOrganizationMemberRoleApi(selectedOrg._id, targetUserId, role);
      if (response.success) {
        showNotification('success', 'Member role updated');
        await loadMembers(selectedOrg._id);
      } else {
        showNotification('error', response.message || 'Failed to update role');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (targetUserId) => {
    if (!selectedOrg) return;
    if (!window.confirm('Remove this member from the organization?')) return;
    try {
      const response = await removeOrganizationMemberApi(selectedOrg._id, targetUserId);
      if (response.success) {
        showNotification('success', 'Member removed');
        await loadMembers(selectedOrg._id);
      } else {
        showNotification('error', response.message || 'Failed to remove member');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;
    try {
      setSaving(true);
      const response = await deleteOrganizationApi(selectedOrg._id);
      if (response.success) {
        showNotification('success', 'Organization deleted');
        setDeleteConfirm(false);
        await loadOrganizations();
      } else {
        showNotification('error', response.message || 'Failed to delete organization');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to delete organization');
    } finally {
      setSaving(false);
    }
  };

  const catalogUrl = selectedOrg
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/o/${selectedOrg.slug}`
    : '';

  const copyCatalogLink = () => {
    navigator.clipboard.writeText(catalogUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-teal-600 mb-4" />
          <p className="text-slate-500">Loading your organization...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification.show && (
        <div className={`px-4 py-3 rounded-2xl border flex items-center gap-3 text-sm font-medium shadow-sm ${
          notification.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'
        }`}>
          <i className={`ri-${notification.type === 'success' ? 'check-circle' : 'error-warning'}-line text-xl flex-shrink-0`} />
          <span>{notification.message}</span>
        </div>
      )}

      {organizations.length === 0 ? (
        // ---------- No organization yet: onboarding ----------
        <div className="max-w-xl mx-auto bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center text-teal-600 text-2xl mb-4">
            <i className="ri-building-2-line" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Create your organization</h2>
          <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            An organization is your own branded space on LearnHub -- a solo tutor is simply an
            organization of one. You'll get a branded course catalog page, and you can invite
            teammates and assign them roles as you grow.
          </p>

          <form onSubmit={handleCreateOrganization} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Organization name *</label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Dominion Chapel International Churches"
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Catalog URL slug (optional)</label>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">/o/</span>
                <input
                  type="text"
                  value={createForm.slug}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder={slugPreview(createForm.name) || 'your-org'}
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Leave blank to auto-generate one from the name.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Logo (optional)</label>
              <div className="flex items-center gap-3">
                {createLogoPreview ? (
                  <img src={createLogoPreview} alt="logo preview" className="w-14 h-14 rounded-xl object-cover border border-slate-200 flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300 flex-shrink-0">
                    <i className="ri-image-line text-xl" />
                  </div>
                )}
                <label className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer transition-colors">
                  Upload image
                  <input type="file" accept="image/*" onChange={handleCreateLogoSelect} className="hidden" />
                </label>
              </div>
              <p className="text-xs text-slate-400 mt-1.5 mb-2">Or paste a link instead:</p>
              <input
                type="url"
                value={createForm.logoUrl}
                onChange={(e) => setCreateForm(prev => ({ ...prev, logoUrl: e.target.value }))}
                placeholder="https://..."
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !createForm.name.trim()}
              className="w-full py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Creating...' : 'Create Organization'}
            </button>
          </form>
        </div>
      ) : (
        // ---------- Has at least one organization ----------
        <div className="space-y-6">
          {organizations.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {organizations.map(org => (
                <button
                  key={org._id}
                  onClick={() => setSelectedOrgId(org._id)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                    org._id === selectedOrgId
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {org.name}
                </button>
              ))}
            </div>
          )}

          {selectedOrg && settingsForm && (
            <>
              {/* Catalog link */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Public catalog page</p>
                  <p className="text-sm font-medium text-slate-800">{catalogUrl}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/o/${selectedOrg.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                  >
                    <i className="ri-external-link-line mr-1" /> View
                  </a>
                  <button
                    onClick={copyCatalogLink}
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-teal-50 hover:bg-teal-100 text-teal-700 transition-colors"
                  >
                    <i className={copiedLink ? 'ri-check-line mr-1' : 'ri-link-m mr-1'} />
                    {copiedLink ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>

              {/* Branding settings */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-1">Branding</h3>
                <p className="text-sm text-slate-500 mb-5">
                  {canManage ? 'Customize how your organization appears on its catalog page.' : `You're a ${ROLE_LABELS[myRole]?.toLowerCase()} here -- only an owner or admin can change these.`}
                </p>
                <form onSubmit={handleSaveSettings} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Name</label>
                    <input
                      type="text"
                      disabled={!canManage}
                      value={settingsForm.name}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Logo</label>
                    <div className="flex items-center gap-3">
                      {(settingsLogoPreview || settingsForm.logoUrl) ? (
                        <img
                          src={settingsLogoPreview || settingsForm.logoUrl}
                          alt="logo preview"
                          className="w-14 h-14 rounded-xl object-cover border border-slate-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300 flex-shrink-0">
                          <i className="ri-image-line text-xl" />
                        </div>
                      )}
                      {canManage && (
                        <label className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer transition-colors">
                          Upload image
                          <input type="file" accept="image/*" onChange={handleSettingsLogoSelect} className="hidden" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5 mb-2">Or paste a link instead:</p>
                    <input
                      type="url"
                      disabled={!canManage}
                      value={settingsForm.logoUrl}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, logoUrl: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Catalog page background image</label>
                    <p className="text-xs text-slate-400 mb-2">Shown behind the hero on your public catalog page, over the background color below.</p>
                    <div className="flex items-center gap-3">
                      {(settingsBgImagePreview || settingsForm.backgroundImageUrl) ? (
                        <img
                          src={settingsBgImagePreview || settingsForm.backgroundImageUrl}
                          alt="background preview"
                          className="w-24 h-14 rounded-xl object-cover border border-slate-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-24 h-14 rounded-xl bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300 flex-shrink-0">
                          <i className="ri-image-2-line text-xl" />
                        </div>
                      )}
                      {canManage && (
                        <label className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer transition-colors">
                          Upload image
                          <input type="file" accept="image/*" onChange={handleSettingsBgImageSelect} className="hidden" />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Primary color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          disabled={!canManage}
                          value={settingsForm.primaryColor}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="w-11 h-11 rounded-xl border border-slate-200 disabled:opacity-50"
                        />
                        <input
                          type="text"
                          disabled={!canManage}
                          value={settingsForm.primaryColor}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                          className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 disabled:bg-slate-50"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Background color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          disabled={!canManage}
                          value={settingsForm.backgroundColor}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          className="w-11 h-11 rounded-xl border border-slate-200 disabled:opacity-50"
                        />
                        <input
                          type="text"
                          disabled={!canManage}
                          value={settingsForm.backgroundColor}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 disabled:bg-slate-50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <h4 className="text-sm font-bold text-slate-800 mb-1">Certificate</h4>
                    <p className="text-xs text-slate-500 mb-4">
                      Applied to every certificate issued across all of this organization's courses (each course can still be switched off individually from its editor).
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Certificate title</label>
                        <input
                          type="text"
                          disabled={!canManage}
                          value={settingsForm.certificateTitle}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, certificateTitle: e.target.value }))}
                          placeholder="Certificate of Completion"
                          className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Signature name</label>
                        <input
                          type="text"
                          disabled={!canManage}
                          value={settingsForm.certificateSignatureName}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, certificateSignatureName: e.target.value }))}
                          placeholder="e.g. Jane Doe"
                          className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Congratulatory message</label>
                      <p className="text-xs text-slate-400 mb-1.5">Shown after the student's name -- e.g. "&lt;Name&gt; {settingsForm.certificateMessage.trim() || 'has successfully completed all requirements for the course'}."</p>
                      <textarea
                        disabled={!canManage}
                        value={settingsForm.certificateMessage}
                        onChange={(e) => setSettingsForm(prev => ({ ...prev, certificateMessage: e.target.value }))}
                        placeholder="has successfully completed all requirements for the course"
                        rows={2}
                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500 resize-none"
                      />
                    </div>
                    <div className="mt-4 max-w-xs">
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Signature title</label>
                      <input
                        type="text"
                        disabled={!canManage}
                        value={settingsForm.certificateSignatureTitle}
                        onChange={(e) => setSettingsForm(prev => ({ ...prev, certificateSignatureTitle: e.target.value }))}
                        placeholder="Course Instructor"
                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                      />
                    </div>
                  </div>

                  {canManage && (
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                </form>
              </div>

              {/* Members */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-bold text-slate-900">Team ({members.length})</h3>
                </div>
                <p className="text-sm text-slate-500 mb-5">
                  {canManage ? 'Invite teammates and manage their roles.' : 'Everyone who has access to this organization.'}
                </p>

                {canManage && (
                  <form onSubmit={handleAddMember} className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email of an existing user</label>
                      <input
                        type="email"
                        value={memberForm.email}
                        onChange={(e) => setMemberForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="teammate@example.com"
                        className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Role</label>
                      <select
                        value={memberForm.role}
                        onChange={(e) => setMemberForm(prev => ({ ...prev, role: e.target.value }))}
                        className="px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500"
                      >
                        {ALL_ROLES.filter(r => r !== 'owner' || isOwner).map(r => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={saving || !memberForm.email.trim()}
                      className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                      Add
                    </button>
                  </form>
                )}

                {membersLoading ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Loading team...</p>
                ) : members.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">No members yet.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map((m) => {
                      const label = m.user?.firstName && m.user?.lastName
                        ? `${m.user.firstName} ${m.user.lastName}`
                        : m.user?.userName || m.user?.email || m.user?.userId;
                      const isMe = m.user?.userId === myUserId;
                      return (
                        <div key={m.user?.userId} className="flex items-center justify-between gap-3 p-3 rounded-2xl border border-slate-100">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">
                              {label} {isMe && <span className="text-slate-400 font-normal">(you)</span>}
                            </p>
                            {m.user?.email && <p className="text-xs text-slate-400 truncate">{m.user.email}</p>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {canManage && !isMe ? (
                              <select
                                value={m.role}
                                onChange={(e) => handleRoleChange(m.user?.userId, e.target.value)}
                                className={`text-xs font-bold px-2.5 py-1.5 rounded-full border ${ROLE_STYLES[m.role]}`}
                              >
                                {ALL_ROLES.filter(r => r !== 'owner' || isOwner).map(r => (
                                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${ROLE_STYLES[m.role]}`}>
                                {ROLE_LABELS[m.role]}
                              </span>
                            )}
                            {canManage && !isMe && (
                              <button
                                onClick={() => handleRemoveMember(m.user?.userId)}
                                className="text-rose-500 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50"
                                title="Remove member"
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Danger zone */}
              {isOwner && (
                <div className="bg-white rounded-3xl border border-rose-100 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-rose-700 mb-1">Danger Zone</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Deleting an organization is permanent. You must move or delete its courses first.
                  </p>
                  {!deleteConfirm ? (
                    <button
                      onClick={() => setDeleteConfirm(true)}
                      className="px-5 py-2.5 bg-rose-50 text-rose-600 text-sm font-semibold rounded-xl hover:bg-rose-100 transition-colors"
                    >
                      Delete Organization
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleDeleteOrganization}
                        disabled={saving}
                        className="px-5 py-2.5 bg-rose-600 text-white text-sm font-semibold rounded-xl hover:bg-rose-700 disabled:opacity-50 transition-colors"
                      >
                        {saving ? 'Deleting...' : 'Yes, permanently delete'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(false)}
                        className="px-5 py-2.5 bg-slate-100 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
